import { Maatregel, VsmStatus } from "@/entities/vsm";
import { VsmActieDto, VsmActieStatusDto } from "@/shared/types";
import { parseISO } from "date-fns";
import { ActieDtoTransformatieError } from "./actie-error";
import { BaseActie } from "./base-actie";

export class VsmActieTransformatieError extends ActieDtoTransformatieError {
    private constructor(wijzigingType: BaseActie["wijzigingType"], reason: string) {
        super("Vsm", wijzigingType, reason);
    }

    public static geenNieuweWaardeVoorToegevoegd(): VsmActieTransformatieError {
        return new VsmActieTransformatieError("Toegevoegd", `nieuweWaarde vereist is, maar deze is: nieuweWaarde=null`);
    }

    public static geenNieuweWaardeVoorGewijzigd(): VsmActieTransformatieError {
        return new VsmActieTransformatieError("Gewijzigd", `nieuweWaarde is vereist, maar deze is: `);
    }
}

export type VsmActieDetails = {
    spoorwebId: string;
    status: "Definitief" | "Toegedeeld" | "Gestopt";
    maatregelen: Maatregel[];
};

export const VsmActieDetails = {
    fromDto: (dto: VsmActieStatusDto): VsmActieDetails => ({
        spoorwebId: dto.spoorwebId,
        status: dto.status,
        maatregelen: dto.maatregelen.map((maatregel) => Maatregel.fromDto(maatregel, true)),
    }),
};

export type VsmToegevoegdActie = BaseActie & {
    type: "Vsm";
    wijzigingType: "Toegevoegd";
    nieuweWaarde: VsmActieDetails;
};

export const VsmToegevoegdActie = {
    fromDto(dto: VsmActieDto): VsmToegevoegdActie {
        if (!dto.nieuweWaarde) {
            throw VsmActieTransformatieError.geenNieuweWaardeVoorToegevoegd();
        }

        const vsmType = dto.nieuweWaarde.status === "Definitief" ? "VSM-D" : "VSM-T";
        return {
            id: dto.id,
            type: "Vsm",
            timelineContent: {
                titel: `${vsmType} toevoegen`,
                beschrijving: "Een VSM is toegevoegd in Spoorweb",
                actieTekst: "Controleer en bevestig VSM",
            },
            dialogContent: {
                titel: "Nieuwe versperringsmaatregel beschikbaar",
                beschrijving:
                    "Er is een nieuwe VSM voor dit dossier beschikbaar. Bekijk de details hieronder. Je kunt treinseries toevoegen.",
                weigerenTitel: "Versperringsmaatregel weigeren?",
                weigerenBeschrijving: "Weet je zeker dat je de versperringsmaatregel wilt weigeren?",
                weigerenActieTekst: "VSM weigeren",
            },
            verstuurdOpDatumTijd: parseISO(dto.verstuurdOpDatumTijd),
            toelichting: dto.toelichting,
            toelichtingLabel: "Toelichting VSM",
            status: dto.status,
            wijzigingType: "Toegevoegd",
            nieuweWaarde: VsmActieDetails.fromDto(dto.nieuweWaarde),
        };
    },
};

export type VsmGewijzigdActie = BaseActie & {
    type: "Vsm";
    wijzigingType: "Gewijzigd";
    nieuweWaarde: VsmActieDetails;
};

export const VsmGewijzigdActie = {
    fromDto(dto: VsmActieDto): VsmGewijzigdActie {
        if (!dto.nieuweWaarde) {
            throw VsmActieTransformatieError.geenNieuweWaardeVoorGewijzigd();
        }

        const vsmType = dto.nieuweWaarde.status === "Definitief" ? "VSM-D" : "VSM-T";
        return {
            id: dto.id,
            type: "Vsm",
            timelineContent: {
                titel: `${vsmType} toevoegen`,
                beschrijving: "Een VSM is geüpdatet in Spoorweb",
                actieTekst: "Controleer en bevestig VSM",
            },
            dialogContent: {
                titel: "Nieuwe versperringsmaatregel beschikbaar",
                beschrijving:
                    "Er is een nieuwe VSM voor dit dossier beschikbaar. Vergelijk de oude en nieuwe versie hieronder. Je kunt treinseries toevoegen.",
                weigerenTitel: "Versperringsmaatregel weigeren?",
                weigerenBeschrijving: "Weet je zeker dat je de versperringsmaatregel wilt weigeren?",
                weigerenActieTekst: "VSM weigeren",
            },
            verstuurdOpDatumTijd: parseISO(dto.verstuurdOpDatumTijd),
            toelichting: dto.toelichting,
            toelichtingLabel: "Toelichting VSM",
            status: dto.status,
            wijzigingType: "Gewijzigd",
            nieuweWaarde: VsmActieDetails.fromDto(dto.nieuweWaarde),
        };
    },
};

export type VsmActie = VsmToegevoegdActie | VsmGewijzigdActie;

export const VsmActie = {
    fromDto: (dto: VsmActieDto): VsmActie => {
        if (dto.wijzigingType === "Toegevoegd") {
            return VsmToegevoegdActie.fromDto(dto);
        } else if (dto.wijzigingType === "Gewijzigd") {
            return VsmGewijzigdActie.fromDto(dto);
        } else {
            throw ActieDtoTransformatieError.nietOndersteundWijzigingTypeVoorActie(dto.type, dto.wijzigingType);
        }
    },
    update: (actie: VsmActie, maatregelen: Maatregel[], toelichting: string | undefined, status: VsmStatus): VsmActie => {
        const filterRijdtGroep = (m: Maatregel) => m.type !== "RijdtGroep";
        const nieuweWaarde = { ...actie.nieuweWaarde, maatregelen: maatregelen.filter(filterRijdtGroep), status };
        return { ...actie, nieuweWaarde, toelichting: toelichting ?? actie.toelichting };
    },
    toDto: (actie: VsmActie): VsmActieDto => ({
        id: actie.id,
        type: actie.type,
        verstuurdOpDatumTijd: actie.verstuurdOpDatumTijd.toISOString(),
        status: actie.status,
        toelichting: actie.toelichting,
        nieuweWaarde: {
            spoorwebId: actie.nieuweWaarde.spoorwebId,
            status: actie.nieuweWaarde.status,
            maatregelen: actie.nieuweWaarde.maatregelen.map(Maatregel.toDto),
        } as VsmActieDto["nieuweWaarde"],
        wijzigingType: actie.wijzigingType,
    }),
};
